import { createFileRoute, Link } from "@tanstack/react-router";
import { useSiteData } from "@/lib/use-site-data";
import { getEventoCarrera } from "@/lib/carrera.functions";
import { CarreraPromocion } from "@/components/eventos/CarreraPromocion";
import salonAsset from "../assets/casa/salon.webp.asset.json";
import fachadaAsset from "../assets/casa/fachada.webp.asset.json";
import fachada480Asset from "../assets/casa/fachada-480.webp.asset.json";
import fachada720Asset from "../assets/casa/fachada-720.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hab1Asset from "../assets/casa/habitacion-1.webp.asset.json";
import comedorAsset from "../assets/casa/comedor.webp.asset.json";
import hab2Asset from "../assets/casa/habitacion-2.webp.asset.json";
import banoAsset from "../assets/casa/bano.webp.asset.json";
import cocinaAsset from "../assets/casa/cocina.webp.asset.json";


const salon = salonAsset.url;
const heroSrcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const fachadaSrcSet = `${fachada480Asset.url} 480w, ${fachada720Asset.url} 720w, ${fachadaAsset.url} 1040w`;

export const Route = createFileRoute("/")({
  loader: () => getEventoCarrera().catch(() => null),
  head: () => ({
    meta: [
      { title: "Casa rural para grupos en Guadalajara · Pajar de Benito" },
      {
        name: "description",
        content:
          "Casa rural completa hasta 14 personas en la Sierra del Alto Rey (Guadalajara). 6 habitaciones con baño privado, chimenea, a 2 h de Madrid. Reserva directa.",
      },
      { property: "og:title", content: "Casa rural para grupos en Guadalajara · Pajar de Benito" },
      { property: "og:description", content: "Casa de piedra rehabilitada, 6 habitaciones con baño privado, chimenea y cocina equipada. Sierra del Alto Rey, Guadalajara. Hasta 14 personas." },
      { property: "og:url", content: "/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://elpajardebenito.es/" },
      {
        rel: "preload",
        as: "image",
        href: hero640Asset.url,
        imageSrcSet: heroSrcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
  }),
  component: Index,
});

function Index() {
  const { config } = useSiteData();
  const evento = Route.useLoaderData();
  return (
    <>
      {/* HERO */}
      <section className="relative h-[calc(100vh-180px)] min-h-[520px] w-full overflow-hidden">
        <img
          src={hero1280Asset.url}
          srcSet={heroSrcSet}
          sizes="100vw"
          alt="Atardecer dorado sobre la Sierra del Alto Rey, Guadalajara"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/55" />
        <div className="relative z-10 mx-auto flex h-full max-w-6xl flex-col justify-end px-4 pb-12 md:px-6 md:pb-16">
          <span className="mb-4 text-xs uppercase tracking-[0.28em] text-white">
            {config.ciudad} · Guadalajara
          </span>
          <h1 className="max-w-3xl font-serif text-4xl leading-[1.05] text-white md:text-6xl">
            {config.hero_titulo || "Casa rural en la Sierra del Alto Rey, Guadalajara"}
          </h1>
          <p className="mt-5 max-w-xl text-base text-white md:text-lg">
            {config.hero_subtitulo}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/reserva"
              className="rounded-full bg-background px-6 py-3 text-sm font-medium text-primary transition-transform hover:scale-[1.02]"
            >
              Consultar disponibilidad
            </Link>
            <Link
              to="/alojamiento"
              className="rounded-full border border-white/40 px-6 py-3 text-sm font-medium text-white hover:bg-white/10"
            >
              Conocer la casa
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      {evento && <CarreraPromocion evento={evento} />}
      <section className="mx-auto max-w-3xl px-4 py-14 text-center md:px-6 md:py-20">
        <p className="text-xs uppercase tracking-[0.28em] text-accent">Bienvenido</p>
        <h2 className="mt-4 font-serif text-3xl text-foreground md:text-4xl">
          La casa entera, para tu grupo
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{config.alojamiento_intro}</p>
      </section>

      {/* SPLIT */}
      <section className="bg-secondary/40 py-20 md:py-28">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 md:grid-cols-2 md:items-center md:gap-16 md:px-6">
          <img
            src={salon}
            alt="Salón con chimenea, vigas de madera y paredes de piedra"
            width={1600}
            height={1067}
            loading="lazy"
            className="aspect-[4/3] w-full rounded-sm object-cover shadow-xl"
          />
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-accent">La casa</p>
            <h2 className="mt-4 font-serif text-3xl text-foreground md:text-4xl">
              Piedra, madera y luz
            </h2>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Hasta 14 personas en 6 habitaciones, todas con baño privado. Cocina equipada, salón con chimenea de leña y patio compartidos. El confort necesario, sin perder el carácter rural.
            </p>
            <Link
              to="/alojamiento"
              className="mt-7 inline-block border-b border-primary pb-1 text-sm font-medium text-primary hover:border-accent hover:text-accent"
            >
              Ver el alojamiento →
            </Link>
          </div>
        </div>
      </section>

      {/* HABITACIONES */}
      <section className="mx-auto max-w-6xl px-4 py-20 md:px-6 md:py-28">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.28em] text-accent">Pensada para grupos</p>
          <h2 className="mt-3 font-serif text-3xl text-foreground md:text-4xl">
            6 habitaciones con baño privado · zonas comunes para compartir
          </h2>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              img: hab1Asset.url,
              alt: "Dormitorio con cama de matrimonio, vigas de madera y ventana con vistas a la Sierra del Alto Rey",
              titulo: "Dormitorio doble",
              desc: "Camas de matrimonio, ropa de cama de algodón y ventanas con vistas al pueblo. Todos con baño privado completo.",
            },
            {
              img: hab2Asset.url,
              alt: "Dormitorio con camas individuales y decoración rústica en piedra y madera",
              titulo: "Dormitorio twin / familiar",
              desc: "Habitaciones con camas individuales o supletorias, ideales para amigos, hermanos o familias con niños.",
            },
            {
              img: banoAsset.url,
              alt: "Baño privado con ducha de obra y azulejo artesanal",
              titulo: "Baño privado en cada habitación",
              desc: "Todos los dormitorios cuentan con baño completo propio, con ducha, secador y amenities de cortesía.",
            },
            {
              img: salon,
              alt: "Salón con chimenea de leña y sofás en la casa rural",
              titulo: "Salón con chimenea",
              desc: "Zona de estar amplia con chimenea de leña encendida en temporada, perfecta para reunir al grupo tras las rutas.",
            },
            {
              img: comedorAsset.url,
              alt: "Comedor amplio con mesa larga para 14 comensales",
              titulo: "Comedor para 14",
              desc: "Mesa larga para que todo el grupo coma junto, con vajilla completa y menaje para celebraciones.",
            },
            {
              img: cocinaAsset.url,
              alt: "Cocina equipada de la casa rural con horno, vitrocerámica y menaje completo",
              titulo: "Cocina totalmente equipada",
              desc: "Horno, vitrocerámica, lavavajillas, cafetera y menaje para cocinar entre todos como en casa.",
            },
          ].map((h) => (
            <article key={h.titulo} className="group">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={h.img}
                  alt={h.alt}
                  width={1600}
                  height={1067}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <h3 className="mt-4 font-serif text-xl text-foreground">{h.titulo}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.desc}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/alojamiento"
            className="inline-block border-b border-primary pb-1 text-sm font-medium text-primary hover:border-accent hover:text-accent"
          >
            Ver todas las estancias y la galería completa →
          </Link>
        </div>
      </section>

      {/* ENTORNO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 py-20 md:grid-cols-2 md:items-center md:gap-16 md:px-6 md:py-28">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-background/70">El entorno</p>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl">
              Sierra del Alto Rey: silencio, rutas y cielos limpios
            </h2>
            <p className="mt-5 leading-relaxed text-primary-foreground/85">
              Senderos hasta la cima del Alto Rey, pueblos negros y rojos de la arquitectura negra, robledales, observación de aves y una de las zonas con menos contaminación lumínica del centro peninsular.
            </p>
            <Link
              to="/entorno"
              className="mt-7 inline-block border-b border-background/70 pb-1 text-sm font-medium text-background hover:border-accent hover:text-accent"
            >
              Descubrir la zona →
            </Link>
          </div>
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet={`${fachada480Asset.url} 480w, ${fachada720Asset.url} 720w`}
              sizes="100vw"
              type="image/webp"
            />
            <img
              src={fachada720Asset.url}
              srcSet={fachadaSrcSet}
              sizes="50vw"
              alt="Calle empedrada de El Ordial con casas de piedra tradicionales"
              width={1040}
              height={778}
              loading="lazy"
              className="aspect-[4/3] w-full rounded-sm object-cover shadow-2xl"
            />
          </picture>
        </div>
      </section>

      {/* RESEÑAS */}
      <section className="mx-auto max-w-6xl px-4 py-20 md:px-6 md:py-28">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.28em] text-accent">Reseñas</p>
          <h2 className="mt-3 font-serif text-3xl text-foreground md:text-4xl">
            Lo que dicen nuestros huéspedes
          </h2>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              texto: "Un sitio precioso, perfecto para desconectar en familia. La casa es muy acogedora y el entorno espectacular.",
              nombre: "María G.",
            },
            {
              texto: "Fuimos un grupo de 12 amigos y cabíamos de sobra. La cocina y el salón con chimenea son ideales para compartir.",
              nombre: "Javier R.",
            },
            {
              texto: "El trato fue inmejorable y los pueblos de la zona merecen mucho la pena. Volveremos seguro.",
              nombre: "Lucía M.",
            },
          ].map((t) => (
            <article key={t.nombre} className="rounded-sm border border-border bg-secondary/30 p-6">
              <div aria-label="5 sobre 5 estrellas" className="text-accent">★★★★★</div>
              <p className="mt-4 text-foreground/85">"{t.texto}"</p>
              <p className="mt-5 text-sm font-medium text-foreground">{t.nombre}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 text-center">
          <a
            href="https://www.google.com/maps/search/?api=1&query=El+Pajar+de+Benito+El+Ordial"
            target="_blank"
            rel="noopener"
            className="inline-block rounded-full border border-primary px-6 py-3 text-sm font-medium text-primary hover:bg-primary/5"
          >
            Ver reseñas en Google
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6 md:py-32">
        <h2 className="font-serif text-3xl text-foreground md:text-5xl">
          ¿Cuándo te vienes?
        </h2>
        <p className="mt-5 text-lg text-muted-foreground">
          Reserva directa, sin intermediarios. Te respondemos en menos de 24 horas.
        </p>
        <Link
          to="/reserva"
          className="mt-8 inline-block rounded-full bg-primary px-8 py-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Consultar disponibilidad
        </Link>
      </section>
    </>
  );
}
